/**
 * 逻辑流策略编辑器 - 类型定义
 */

// ========== 节点类型枚举 ==========
export type LogicFlowNodeType = 'INDICATOR' | 'RULE';

// ========== 比较运算符 ==========
export type ComparisonOperator = '>' | '<' | '>=' | '<=' | '==' | '!=' | 'cross_above' | 'cross_below';

// ========== 交易动作类型 ==========
export type TradeAction = 'enter_long' | 'enter_short' | 'exit_long' | 'exit_short' | 'none';

// ========== 条件运算符 ==========
export type ConditionOperator = 'AND' | 'OR';

// ========== 节点配置 ==========
export interface IndicatorParam {
    name: string;
    value: string;
    type: 'number' | 'string' | 'column' | 'expression';
}

export type IndicatorMode = 'standard' | 'custom' | 'raw';

export type IndicatorOutputType = 'column' | 'Dict' | 'List' | 'DataFrame';

export interface IndicatorNodeConfig {
    type: 'INDICATOR';
    mode?: IndicatorMode;
    outputType?: IndicatorOutputType;  // 输出类型：默认 'column'（写入 dataframe 列）
    indicatorName: string;
    function: string;
    params: IndicatorParam[];
    description?: string;
    outputKey?: string;         // 多输出指标的子键，如 bollinger['lower']
    rawExpression?: string;     // 源码解析时保留的原始右值表达式
    rawDependencies?: string[]; // rawExpression 依赖的中间变量赋值语句
    customExpression?: string;  // 用户自定义计算表达式（自定义模式）
}

export interface ConditionRule {
    leftOperand: string;
    operator: ComparisonOperator;
    rightOperand: string;
}

export interface RuleNodeConfig {
    type: 'RULE';
    logicOperator: ConditionOperator;
    rules: ConditionRule[];
    action: TradeAction;
    signalValue: string;
}

export type LogicFlowNodeConfig = IndicatorNodeConfig | RuleNodeConfig;

// ========== 节点和边 ==========
export interface LogicFlowNode {
    id: string;
    type: LogicFlowNodeType;
    label: string;
    config: LogicFlowNodeConfig;
    x?: number;
    y?: number;
}

export interface LogicFlowEdge {
    id: string;
    source: string;
    target: string;
    sourceHandle?: string;
    targetHandle?: string;
    label?: string;
}

// ========== 图结构 ==========
export interface LogicFlowGraph {
    nodes: LogicFlowNode[];
    edges: LogicFlowEdge[];
}

// ========== 策略全局配置 ==========
export interface StrategyConfig {
    timeframe: string;
    canShort: boolean;
    stoploss: string;
    minimalRoi: Record<string, number>;
}

// ========== V2 可视化模型 ==========
export interface StrategyVisualModelV2 {
    schemaVersion: '2.0';
    editorType: 'logic-flow';
    title: string;
    description: string;
    strategyName: string;
    strategyClassName: string;
    config: StrategyConfig;
    graph: LogicFlowGraph;
    createdAt?: string;
    updatedAt?: string;
}

// ========== 默认配置 ==========
export const DEFAULT_STRATEGY_CONFIG: StrategyConfig = {
    timeframe: '5m',
    canShort: false,
    stoploss: '-0.10',
    minimalRoi: {'0': 0.10},
};

// ========== 默认空模型 ==========
export function createDefaultVisualModelV2(): StrategyVisualModelV2 {
    return {
        schemaVersion: '2.0',
        editorType: 'logic-flow',
        title: '',
        description: '',
        strategyName: 'NewStrategy',
        strategyClassName: 'NewStrategy',
        config: {...DEFAULT_STRATEGY_CONFIG},
        graph: {nodes: [], edges: []},
    };
}

// ========== 指标函数库 ==========
export const INDICATOR_FUNCTION_LIBRARY: Array<{
    name: string;
    expression: string;
    params: IndicatorParam[];
    description: string;
    returnsType?: IndicatorOutputType;
    outputKeys?: string[];
}> = [
    {
        name: 'RSI',
        expression: 'ta.RSI',
        params: [
            {name: 'dataframe_column', value: 'close', type: 'column'},
            {name: 'timeperiod', value: '14', type: 'number'},
        ],
        description: '相对强弱指标',
    },
    {
        name: 'EMA',
        expression: 'ta.EMA',
        params: [
            {name: 'dataframe_column', value: 'close', type: 'column'},
            {name: 'timeperiod', value: '20', type: 'number'},
        ],
        description: '指数移动平均线',
    },
    {
        name: 'SMA',
        expression: 'ta.SMA',
        params: [
            {name: 'dataframe_column', value: 'close', type: 'column'},
            {name: 'timeperiod', value: '20', type: 'number'},
        ],
        description: '简单移动平均线',
    },
    {
        name: 'MACD',
        expression: 'ta.MACD',
        params: [
            {name: 'fastperiod', value: '12', type: 'number'},
            {name: 'slowperiod', value: '26', type: 'number'},
            {name: 'signalperiod', value: '9', type: 'number'},
        ],
        description: 'MACD 指标（返回 macd/macdsignal/macdhist）',
        returnsType: 'Dict',
        outputKeys: ['macd', 'macdsignal', 'macdhist'],
    },
    {
        name: 'BBANDS',
        expression: 'ta.BBANDS',
        params: [
            {name: 'timeperiod', value: '20', type: 'number'},
            {name: 'nbdevup', value: '2', type: 'number'},
            {name: 'nbdevdn', value: '2', type: 'number'},
        ],
        description: '布林带（返回 upper/middle/lower）',
        returnsType: 'Dict',
        outputKeys: ['upper', 'middle', 'lower'],
    },
    {
        name: 'ATR',
        expression: 'ta.ATR',
        params: [
            {name: 'timeperiod', value: '14', type: 'number'},
        ],
        description: '平均真实波幅',
    },
    {
        name: 'ADX',
        expression: 'ta.ADX',
        params: [
            {name: 'timeperiod', value: '14', type: 'number'},
        ],
        description: '平均趋向指数',
    },
    {
        name: 'STOCH',
        expression: 'ta.STOCH',
        params: [
            {name: 'fastk_period', value: '14', type: 'number'},
            {name: 'slowk_period', value: '3', type: 'number'},
            {name: 'slowd_period', value: '3', type: 'number'},
        ],
        description: '随机指标（返回 slowk/slowd）',
        returnsType: 'Dict',
        outputKeys: ['slowk', 'slowd'],
    },
    {
        name: 'CCI',
        expression: 'ta.CCI',
        params: [
            {name: 'timeperiod', value: '14', type: 'number'},
        ],
        description: '商品通道指数',
    },
    {
        name: 'OBV',
        expression: 'ta.OBV',
        params: [],
        description: '能量潮指标',
    },
    {
        name: 'SAR',
        expression: 'ta.SAR',
        params: [
            {name: 'high', value: 'high', type: 'column'},
            {name: 'low', value: 'low', type: 'column'},
        ],
        description: '抛物线 SAR 指标',
    },
    {
        name: 'CDLHAMMER',
        expression: 'ta.CDLHAMMER',
        params: [],
        description: 'K线形态-锤子线',
    },
    {
        name: 'Bollinger Bands (qt)',
        expression: 'qtpylib.bollinger_bands',
        params: [
            {name: 'source', value: 'close', type: 'column'},
            {name: 'window', value: '20', type: 'number'},
            {name: 'stds', value: '2', type: 'number'},
        ],
        description: '布林带 (qtpylib)',
        returnsType: 'Dict',
    },
    {
        name: 'Typical Price',
        expression: 'qtpylib.typical_price',
        params: [
            {name: 'dataframe_column', value: 'close', type: 'column'},
        ],
        description: '典型价格 (qtpylib)',
    },
];

// ========== 连接约束 ==========
const VALID_CONNECTIONS: Record<LogicFlowNodeType, LogicFlowNodeType[]> = {
    INDICATOR: ['INDICATOR', 'RULE'],
    RULE: ['RULE'],
};

export function isValidConnection(sourceType: LogicFlowNodeType, targetType: LogicFlowNodeType): boolean {
    const allowed = VALID_CONNECTIONS[sourceType];
    return allowed != null && allowed.includes(targetType);
}

// ========== 环检测 ==========
export function hasCycle(graph: LogicFlowGraph): boolean {
    const adj = new Map<string, string[]>();
    for (const node of graph.nodes) {
        adj.set(node.id, []);
    }
    for (const edge of graph.edges) {
        const neighbors = adj.get(edge.source);
        if (neighbors) {
            neighbors.push(edge.target);
        }
    }

    const visiting = new Set<string>();
    const visited = new Set<string>();

    function dfs(nodeId: string): boolean {
        if (visiting.has(nodeId)) {
            return true;
        }
        if (visited.has(nodeId)) {
            return false;
        }
        visiting.add(nodeId);
        for (const neighbor of adj.get(nodeId) || []) {
            if (dfs(neighbor)) {
                return true;
            }
        }
        visiting.delete(nodeId);
        visited.add(nodeId);
        return false;
    }

    for (const node of graph.nodes) {
        if (dfs(node.id)) {
            return true;
        }
    }
    return false;
}
